import { CalculatedFieldDefinition, SurveyDefinition } from "../../../types/Survey";
import { FileResultModel, QuestionResultModel, StepResultModel, SurveyResultModel } from "../../../types/SurveyResult";
import { evaluateExpression, ExpressionInputs, extractDependencies, ResultType, SurveyAnswerDTO, validateReturnType } from "@careevolution/mydatahelps-cel"
import { getAnswerTypeMappings, SurveyAnswerTypeMapping } from "./answerTypeMappings";
import { convertAnswerToType } from "./answerTypeConversion";
import { formatISO } from "date-fns";
export const calculatedFieldStepIdentifier = "_calculated_fields";

export function evaluateCalculatedFields(
    surveyDefinition: SurveyDefinition,
    baseInputs: ExpressionInputs,
    surveyResult: SurveyResultModel): StepResultModel | null {
    if (!surveyDefinition.calculatedFields || surveyDefinition.calculatedFields.length === 0) {
        return null;
    }

    const answerTypeMappings = getAnswerTypeMappings(surveyDefinition);
    const now = formatISO(new Date(), { representation: "complete" })
    const finalStepResult: StepResultModel = {
        Identifier: calculatedFieldStepIdentifier,
        StartDate: now,
        EndDate: now,
        QuestionResults: []
    };

    //filter out any existing calculated field results in case this is an edit
    const combinedResult = [...surveyResult.StepResults.filter(f => f.Identifier !== calculatedFieldStepIdentifier), finalStepResult];

    surveyDefinition.calculatedFields.forEach(field => {
        const inputs = {
            ...baseInputs,
        };

        inputs.currentSurveyContext!.currentSurveyAnswers = getCurrentAnswers(combinedResult, answerTypeMappings, surveyResult.Event);

        try {
            const result = evaluateExpression(field.expression.expression, inputs, field.returnType);
            let answers = [];
            if (field.returnType.startsWith("List")) {
                answers = result.map((r: any) => String(r));
            } else {
                answers = [String(result)];
            }

            // Store the calculated result back into the variables for potential use in subsequent calculations
            finalStepResult.QuestionResults.push({
                Type: "QuestionResult",
                Identifier: field.identifier,
                StartDate: now,
                EndDate: now,
                Answers: answers
            } as QuestionResultModel);
        } catch (error: any) {
            // do nothing on error - values may not yet be present, so errors are expected
        }
    });

    return finalStepResult;
}

export function evaluatePredicateExpression(
    expression: string,
    surveyDefinition: SurveyDefinition,
    baseInputs: ExpressionInputs,
    surveyResult: SurveyResultModel): boolean {

    const answerTypeMappings = getAnswerTypeMappings(surveyDefinition);
    const inputs = {
        ...baseInputs,
    };
    inputs.currentSurveyContext!.currentSurveyAnswers = getCurrentAnswers(surveyResult.StepResults, answerTypeMappings, surveyResult.Event);

    try {
        const result = evaluateExpression(expression, inputs, "Boolean");
        return Boolean(result);
    } catch (error: any) {
        return false;
    }
}

function getCurrentAnswers(stepResults: StepResultModel[], answerTypeMappings: SurveyAnswerTypeMapping[], event?: string): SurveyAnswerDTO[] {
    return stepResults.flatMap(step => {
        return step.QuestionResults.filter(q => q.Type == "QuestionResult" || q.Type == "FileResult").map(r => {
            let answers: string[] = [];
            if (r.Type === "FileResult") {
                const fr = r as FileResultModel;
                answers = fr.FileName ? [fr.FileName] : [];
            } else {
                const qr = r as QuestionResultModel;
                answers = qr.Answers;
            }

            const answerType = answerTypeMappings.find(a => a.resultIdentifier === r.Identifier && a.stepIdentifier === step.Identifier)?.answerType ?? "Text";

            return {
                stepIdentifier: step.Identifier,
                resultIdentifier: r.Identifier,
                answer: convertAnswerToType(answers, answerType),
                event: event
            };
        });
    });
}

export function validateCalculatedField(calculatedField: CalculatedFieldDefinition, template: SurveyDefinition): string[] {
    const validationErrors: string[] = [];
    if (!calculatedField.identifier) {
        validationErrors.push("Calculated field must have an identifier");
    }
    if (calculatedField.identifier && calculatedField.identifier.includes(".")) {
        validationErrors.push("Calculated field identifier cannot contain '.'");
    }
    if (template.calculatedFields?.some(field => field !== calculatedField && field.identifier === calculatedField.identifier)) {
        validationErrors.push("Calculated field identifier must be unique");
    }

    const validationError = validateExpression(calculatedField.expression.expression, calculatedField.returnType);
    if (validationError) {
        validationErrors.push(validationError);
    }
    return validationErrors;
}

export function validateExpression(expression: string, returnType: ResultType): string | null {
    if (!expression || expression.trim() === "") {
        return "Expression cannot be empty";
    }
    try {
        extractDependencies([expression]);
    } catch (error: any) {
        return `${error.message}`;
    }

    return validateReturnType(expression, returnType);
}